import { t } from "@redotech/redo-i18n";
import { Step as ModelStep } from "@redotech/redo-return-core-common/return-flow";
import { dynamicBundleAdjustmentFromModel } from "@redotech/redo-return-core-common/return-flow-step-from-model";
import {
  DynamicBundleAdjustmentState,
  isDynamicBundleAdjustmentStepValid,
} from "@redotech/redo-return-core-common/return-flow-step-valid";
import { STEP_TYPE_NAMES } from "@redotech/redo-return-core-common/return-flow/step-type-names";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import MoneyIcon from "@redotech/redo-web/icon-old/share-money.svg";
import { produce } from "immer";
import * as React from "react";
import { StepDownstream, StepType, StepTypeDetailsProps } from "../step";

export const DYNAMIC_BUNDLE_ADJUSTMENT = (
  Details: React.ComponentType<
    StepTypeDetailsProps<DynamicBundleAdjustmentState>
  >,
): StepType<
  DynamicBundleAdjustmentState,
  ModelStep.DynamicBundleAdjustment
> => ({
  Details: (props: StepTypeDetailsProps<DynamicBundleAdjustmentState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return state.customTitle;
  },
  title: STEP_TYPE_NAMES[ModelStep.DYNAMIC_BUNDLE_ADJUSTMENT],
  valid(state) {
    return isDynamicBundleAdjustmentStepValid(state).valid;
  },
  Icon: MoneyIcon,
  iconColor: "success",
  description(state) {
    return t("Dynamic bundle adjustment");
  },
  downstream(state) {
    const result: StepDownstream[] = [];
    if (state.next !== undefined) {
      result.push({ id: state.next });
    }
    return result;
  },
  maxDownstream() {
    return 1;
  },
  empty: { next: undefined },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      if (state.next === stepId) {
        state.next = undefined;
      }
    });
  },
  updateConnection(state, handleIndex, targetStepId) {
    if (handleIndex !== 0) {
      throw new Error(
        `Invalid handle index ${handleIndex} for Dynamic Bundle Adjustment step`,
      );
    }
    return produce(state, (draft) => {
      draft.next = targetStepId;
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.next === targetStepId) {
        draft.next = undefined;
      }
    });
  },
  layout() {
    return BlockLayout.FULL;
  },
  toModel(state, id) {
    return {
      customTitle: state.customTitle,
      type: ModelStep.DYNAMIC_BUNDLE_ADJUSTMENT,
      next: state.next !== undefined ? id(state.next) : undefined,
    };
  },
  fromModel(model, id) {
    return dynamicBundleAdjustmentFromModel(model, id);
  },
});
